import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Modal } from 'react-bootstrap';
import { ChevronRight, Eye, EyeSlash } from 'react-bootstrap-icons';
import { isDemoOrDevApp } from '../api';
import './PatientHeader.css';

/** Add-on surgery row saved under appointment_id "0" (not from OPM). */
const SYNTHETIC_APPOINTMENT_ID = '0';

function PatientHeader({
  patient,
  recommendation,
  isLoading,
  compactBottom = false,
  scheduleSlot = null,
  appointmentsAvailable = false,
  appointmentsVisible = false,
  appointmentsCount = 0,
  appointmentsLabel = 'active appointments',
  appointmentsMuted = false,
  onToggleAppointments,
  showApprovalStatus = true,
  listBlurPatientName = false,
  approvalFeedback = null,
  studyWithdrawn = false,
  withdrawBusy = false,
  withdrawError = '',
  withdrawStudyName = 'this study',
  onWithdrawFromStudy = null,
}) {
  const [blurPatientName, setBlurPatientName] = useState(listBlurPatientName);
  const [pillFlashStatus, setPillFlashStatus] = useState(null);
  const [feedbackNotice, setFeedbackNotice] = useState(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  useEffect(() => {
    setBlurPatientName(listBlurPatientName);
  }, [listBlurPatientName]);

  useEffect(() => {
    setConfirmWithdraw(false);
  }, [patient?.id]);

  useEffect(() => {
    if (!approvalFeedback?.status) return undefined;
    setPillFlashStatus(approvalFeedback.status);
    setFeedbackNotice(approvalFeedback);
    const pulseTimer = window.setTimeout(() => setPillFlashStatus(null), 800);
    const noticeTimer = window.setTimeout(() => setFeedbackNotice(null), 2400);
    return () => {
      window.clearTimeout(pulseTimer);
      window.clearTimeout(noticeTimer);
    };
  }, [approvalFeedback?.status, approvalFeedback?.at]);

  if (!patient) return null;

  const findAge = () => {
    if (Array.isArray(recommendation?.inputs)) {
      return recommendation.inputs.find((input) => input.age)?.age;
    }
    return null;
  };

  const isSynthetic =
    recommendation?.appointment_id != null &&
    String(recommendation.appointment_id) === SYNTHETIC_APPOINTMENT_ID;
  const showSyntheticDisclaimer = !isLoading && isSynthetic && !isDemoOrDevApp();

  const approvalStatusLabel = recommendation?.approval_status
    ? recommendation.approval_status.charAt(0).toUpperCase() + recommendation.approval_status.slice(1)
    : 'Pending';

  const patientNameStyle = blurPatientName
    ? { filter: 'blur(7px)', userSelect: 'none', WebkitUserSelect: 'none' }
    : undefined;
  const showWithdraw = Boolean(onWithdrawFromStudy) && !studyWithdrawn;

  return (
    <>
    <Card
      className={`patient-header shadow-sm ${compactBottom ? 'mb-2' : 'mb-3'}`}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <Card.Body className={`py-2 px-3${showWithdraw ? ' patient-header-body--withdraw' : ''}`}>
        <div className={`d-flex flex-wrap align-items-baseline justify-content-between gap-2 gap-md-3${scheduleSlot || showSyntheticDisclaimer ? ' mb-2' : ''}`}>
          <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
            <h5 className="mb-0" style={patientNameStyle}>{patient.name}</h5>
            {withdrawError && (
              <small className="text-danger">{withdrawError}</small>
            )}
            <Button
              variant={blurPatientName ? 'secondary' : 'outline-secondary'}
              size="sm"
              onClick={() => setBlurPatientName((v) => !v)}
              style={{ minWidth: '40px', flexShrink: 0 }}
              className="d-flex align-items-center justify-content-center px-2"
              title={blurPatientName ? 'Show patient name' : 'Blur patient name (privacy)'}
              aria-label={blurPatientName ? 'Show patient name' : 'Blur patient name'}
              aria-pressed={blurPatientName}
            >
              {blurPatientName ? <Eye size={18} /> : <EyeSlash size={18} />}
            </Button>
            {!isLoading && (
              <small className="text-muted">
                Age {findAge() ?? patient?.age ?? 'Unknown'}
              </small>
            )}
            {appointmentsAvailable && !appointmentsVisible && onToggleAppointments && (
              <button
                type="button"
                className={`btn btn-link text-start text-decoration-none p-0 mb-0 d-flex align-items-center gap-2${
                  appointmentsMuted ? ' text-secondary' : ''
                }`}
                onClick={onToggleAppointments}
                aria-expanded={false}
                aria-controls="patient-header-schedule"
                title="Show appointments"
              >
                <ChevronRight size={14} className="flex-shrink-0" aria-hidden />
                <span
                  className="badge rounded-pill px-2 py-1 border small"
                  style={
                    appointmentsMuted
                      ? {
                          background: '#e9ecef',
                          color: '#495057',
                          borderColor: '#ced4da',
                        }
                      : {
                          background: 'var(--brand-primary)',
                          color: 'var(--brand-primary-dark)',
                          borderColor: 'var(--brand-primary-medium)',
                        }
                  }
                >
                  {appointmentsCount}
                </span>
                <span
                  className={`fw-semibold small${appointmentsMuted ? ' text-secondary' : ''}`}
                  style={appointmentsMuted ? undefined : { color: 'var(--brand-primary-dark)' }}
                >
                  {appointmentsLabel}
                </span>
              </button>
            )}
          </div>
          {showApprovalStatus && (
            <div className="text-muted small text-end ms-auto" style={{ minWidth: 0, maxWidth: '100%' }}>
              {isLoading ? (
                'Loading…'
              ) : (
                recommendation && (
                  <>
                    <Badge
                      bg={
                        recommendation.approval_status === 'approved'
                          ? 'success'
                          : recommendation.approval_status === 'denied'
                            ? 'danger'
                            : 'warning'
                      }
                      className={[
                        'patient-header-approval-pill',
                        'align-middle',
                        pillFlashStatus
                          ? `patient-header-approval-pill--flash patient-header-approval-pill--flash-${pillFlashStatus}`
                          : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                        backgroundColor:
                          recommendation.approval_status === 'approved'
                            ? 'var(--action-approve)'
                            : recommendation.approval_status === 'denied'
                              ? 'var(--action-deny)'
                              : 'var(--action-modify)',
                      }}
                    >
                      {approvalStatusLabel}
                    </Badge>
                    {recommendation.approval_time ? (
                      <span className="text-muted">{` · ${new Date(recommendation.approval_time).toLocaleString()}`}</span>
                    ) : null}
                    {feedbackNotice?.status && (
                      <span
                        key={feedbackNotice.at}
                        className={`patient-header-approval-feedback patient-header-approval-feedback--${feedbackNotice.status}`}
                        role="status"
                        aria-live="polite"
                      >
                        {feedbackNotice.status === 'denied' ? 'Denied' : 'Approved'}
                      </span>
                    )}
                  </>
                )
              )}
            </div>
          )}
        </div>
        {showSyntheticDisclaimer && (
          <small className="text-danger d-block w-100 mb-2">
            This procedure was added within Springhouse, not from your scheduling system. Please add a corresponding appointment in your scheduling app.
          </small>
        )}
        {scheduleSlot ? (
          <div id="patient-header-schedule" className="patient-header-schedule">
            {scheduleSlot}
          </div>
        ) : null}
      </Card.Body>
      {showWithdraw && (
        <Button
          variant="danger"
          size="sm"
          className="patient-header-withdraw"
          onClick={() => setConfirmWithdraw(true)}
          disabled={withdrawBusy}
        >
          Withdraw
        </Button>
      )}
    </Card>
    <Modal
      show={confirmWithdraw}
      onHide={() => { if (!withdrawBusy) setConfirmWithdraw(false); }}
      centered
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Withdraw</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Would you like to withdraw this patient from {withdrawStudyName}?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setConfirmWithdraw(false)} disabled={withdrawBusy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={withdrawBusy}
          onClick={() => {
            setConfirmWithdraw(false);
            onWithdrawFromStudy?.();
          }}
        >
          Withdraw
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}

export default PatientHeader;
